import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ServedQuestion } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { keys, useSessionPaper } from '../lib/queries';
import { SM_QUERY, useMediaQuery } from '../lib/useMediaQuery';
import { Callout, ErrorNotice, Spinner } from '../components/ui';

function formatClock(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

/**
 * Keeps the screen on for the duration of a paper.
 *
 * A CAOP paper is two hours and an OPRA paper two and a half, most of which is
 * spent reading rather than touching the screen — long enough that a phone's
 * display timeout will fire repeatedly. The lock is dropped automatically by
 * the browser whenever the page is hidden, so it has to be re-taken on the way
 * back, and the whole thing is best-effort: Safari only shipped the API in 16.4
 * and it rejects outright when the device is in low-power mode.
 */
function useScreenWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let released = false;

    const acquire = async () => {
      if (released || document.visibilityState !== 'visible') return;
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        // Denied, unsupported or low-power mode — the paper works regardless.
      }
    };

    void acquire();
    document.addEventListener('visibilitychange', acquire);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', acquire);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}

export function ExamRunner() {
  const { sessionId = '' } = useParams();
  const { data, isLoading, error } = useSessionPaper(sessionId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isWide = useMediaQuery(SM_QUERY);

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [books, setBooks] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Time on the current question, used as the per-question timing signal.
  const questionStart = useRef<number>(Date.now());

  /**
   * Wall-clock instant the paper closes.
   *
   * The countdown is derived from this rather than decremented, because a
   * phone suspends JS timers the moment the browser is backgrounded or the
   * screen locks: a `setTimeout` chain simply stops counting, so a candidate
   * who took a call mid-paper would come back to a clock that had quietly
   * given them the call's duration back. `Date.now()` keeps advancing through
   * suspension, so anchoring to an instant and re-reading it is the only way
   * the simulation stays honest about time — which is half of what it claims
   * to measure.
   *
   * It is anchored from the server's own `remainingSec` at the moment the
   * paper loaded, not from `startedAt`, so a device clock that disagrees with
   * the server's in absolute terms still counts down the right duration.
   */
  const deadline = useRef<number | null>(null);

  // Hydrate any answers already saved, so a refresh mid-paper loses nothing.
  useEffect(() => {
    if (!data) return;
    const seeded: Record<string, string> = {};
    const seededBooks: Record<string, boolean> = {};
    for (const [qid, v] of Object.entries(data.answers)) {
      if (v.answer !== null) seeded[qid] = v.answer;
      if (v.referencedBook !== null) seededBooks[qid] = v.referencedBook;
    }
    setAnswers(seeded);
    setBooks(seededBooks);
    deadline.current =
      data.session.remainingSec === null ? null : Date.now() + data.session.remainingSec * 1000;
    setRemaining(data.session.remainingSec);
  }, [data]);

  const submit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await api.post<{ benchmarkId: string }>(`/api/sessions/${sessionId}/complete`);
      await qc.invalidateQueries({ queryKey: keys.sessions });
      await qc.invalidateQueries({ queryKey: keys.benchmarks });
      navigate(`/results/${result.benchmarkId}`);
    } catch (err) {
      setSubmitError(err instanceof ApiRequestError ? err.message : 'Could not submit your paper.');
      setSubmitting(false);
    }
  }, [navigate, qc, sessionId]);

  const timed = data?.session.remainingSec !== null && data?.session.remainingSec !== undefined;
  const live = Boolean(data) && !data?.session.completedAt;

  useScreenWakeLock(live);

  // Countdown. Auto-submits at zero, exactly as the real paper would close.
  useEffect(() => {
    if (!live || !timed) return;

    let submitted = false;
    const tick = () => {
      if (deadline.current === null || submitted) return;
      const left = Math.ceil((deadline.current - Date.now()) / 1000);
      setRemaining(Math.max(0, left));
      if (left <= 0) {
        submitted = true;
        void submit();
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    // Coming back from a backgrounded tab: re-read immediately rather than
    // waiting up to a second, so the clock is never visibly wrong — and so an
    // expiry that happened while away submits on return instead of granting a
    // free second of paper.
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [live, timed, submit]);

  const questions = data?.questions ?? [];
  const current = questions[index];
  const answeredCount = useMemo(
    () => questions.filter((q) => answers[q.id] !== undefined && answers[q.id] !== '').length,
    [questions, answers],
  );

  const saveAnswer = async (question: ServedQuestion, value: string, referencedBook?: boolean) => {
    setAnswers((a) => ({ ...a, [question.id]: value }));
    setSaving(true);
    try {
      await api.post(`/api/sessions/${sessionId}/answer`, {
        questionId: question.id,
        answer: value,
        timeTakenSec: Math.round((Date.now() - questionStart.current) / 1000),
        referencedBook: referencedBook ?? books[question.id] ?? false,
      });
    } catch {
      // A dropped save is recoverable — the answer stays in local state and the
      // next navigation or submit will persist it.
    } finally {
      setSaving(false);
    }
  };

  const goTo = (next: number) => {
    setIndex(Math.max(0, Math.min(questions.length - 1, next)));
    questionStart.current = Date.now();
    // Each question is a fresh screenful; on a phone the previous one will
    // have been scrolled well past the stem by the time it is answered.
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  if (isLoading) return <Spinner label="Loading your paper" />;
  if (error || !data || !current) {
    return <ErrorNotice message={error instanceof Error ? error.message : 'Could not load this session.'} />;
  }
  if (data.session.completedAt) {
    return (
      <Callout>
        You have already submitted this paper.{' '}
        <button type="button" className="font-semibold underline" onClick={() => navigate('/history')}>
          View your history
        </button>
      </Callout>
    );
  }

  const isLow = remaining !== null && remaining < 300;
  const atStart = index === 0;
  const atEnd = index === questions.length - 1;

  const submitButton = (className: string) => (
    <button type="button" className={className} onClick={() => void submit()} disabled={submitting}>
      {submitting ? 'Submitting…' : 'Submit paper'}
    </button>
  );

  return (
    /*
     * The bottom padding clears the fixed Prev/Next bar that only exists below
     * `sm`; without it the navigator's last row sits underneath it.
     */
    <div className="mx-auto max-w-4xl pb-28 sm:pb-0">
      {/*
        Sticky status bar: progress, clock, and — above `sm` — submit.
        `bleed-safe` cancels the main element's gutter so the bar spans the
        full width, and `top` tracks the real header height rather than a
        hardcoded pixel count that goes wrong whenever the header changes.
      */}
      <div className="sticky top-[var(--header-h)] z-20 bleed-safe mb-6 border-b border-line bg-surface/95 px-safe py-2.5 backdrop-blur sm:py-3">
        <div className="flex items-center gap-3 sm:flex-wrap sm:gap-x-5 sm:gap-y-2">
          <div className="min-w-0">
            <p className="hidden text-xs font-medium uppercase tracking-wide text-muted sm:block">
              {data.session.examCode}{' '}
              {data.session.mode === 'diagnostic'
                ? 'diagnostic'
                : data.session.mode === 'baseline'
                  ? 'full paper'
                  : 'practice'}
            </p>
            <p className="truncate text-sm font-semibold text-ink">
              <span className="sm:hidden">
                {index + 1}/{questions.length}
              </span>
              <span className="hidden sm:inline">
                Question {index + 1} of {questions.length}
              </span>
              <span className="ml-2 font-normal text-muted">({answeredCount} answered)</span>
            </p>
          </div>

          {remaining !== null ? (
            <div
              className={`ml-auto shrink-0 rounded-lg px-2.5 py-1 tabular-nums sm:px-3 sm:py-1.5 ${isLow ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300' : 'bg-raised text-muted'}`}
            >
              <span className="hidden text-xs font-medium uppercase tracking-wide sm:inline">Time left </span>
              <span className="text-base font-bold sm:text-lg">{formatClock(remaining)}</span>
            </div>
          ) : (
            <div className="ml-auto shrink-0 text-sm text-muted">Untimed</div>
          )}

          {/*
            Below `sm` submitting lives at the foot of the paper instead. It is
            a once-per-session, irreversible action, and putting it a thumb's
            width from the clock on a phone is how a paper gets ended by
            accident twenty questions early.
          */}
          {submitButton('btn-primary hidden shrink-0 sm:inline-flex')}
        </div>

        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-line sm:mt-2.5">
          <div
            className="h-full rounded-full bg-brand-600 transition-all"
            style={{ width: `${(answeredCount / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {submitError ? <div className="mb-4"><ErrorNotice message={submitError} /></div> : null}

      <article className="card p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="badge bg-raised text-muted">
            {current.domainCode} · {current.domainName}
          </span>
          <span className="badge bg-raised text-muted capitalize">{current.difficulty}</span>
          {current.questionType === 'fib' ? (
            <span className="badge bg-brand-50 dark:bg-brand-500/15 text-brand-800 dark:text-brand-300">Calculation</span>
          ) : null}
        </div>

        {/* Determiners arrive already wrapped in ** by the generator. */}
        <p className="mt-4 whitespace-pre-wrap text-base leading-relaxed text-ink">
          <Stem text={current.stem} />
        </p>

        <div className="mt-6">
          {current.questionType === 'mcq' && current.options ? (
            <ul className="space-y-2.5">
              {current.options.map((option, i) => {
                const selected = answers[current.id] === option;
                return (
                  <li key={option}>
                    <label
                      className={`flex cursor-pointer gap-3 rounded-lg border p-4 text-sm transition ${
                        selected ? 'border-brand-600 bg-brand-50 dark:bg-brand-500/15 ring-1 ring-brand-600' : 'border-line hover:bg-raised'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q-${current.id}`}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700"
                        checked={selected}
                        onChange={() => void saveAnswer(current, option)}
                      />
                      <span className="font-medium text-muted">{String.fromCharCode(65 + i)}.</span>
                      <span className="text-ink">{option}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="max-w-sm">
              <label className="label" htmlFor="fib">
                Your answer{current.unit ? ` (${current.unit})` : ''}
              </label>
              <input
                id="fib"
                inputMode="decimal"
                className="input font-mono text-lg"
                placeholder={current.decimalPlaces !== null ? (0).toFixed(current.decimalPlaces) : '0'}
                value={answers[current.id] ?? ''}
                onChange={(e) => setAnswers((a) => ({ ...a, [current.id]: e.target.value }))}
                onBlur={(e) => void saveAnswer(current, e.target.value)}
              />
              {current.decimalPlaces !== null ? (
                <p className="mt-1.5 text-xs text-muted">
                  Give your answer to {current.decimalPlaces} decimal place{current.decimalPlaces === 1 ? '' : 's'}.
                </p>
              ) : null}
            </div>
          )}
        </div>

        {data.session.bookUseTracked ? (
          <label className="mt-6 flex cursor-pointer items-start gap-2.5 rounded-lg border border-line bg-raised p-3.5">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-700"
              checked={books[current.id] ?? false}
              onChange={(e) => {
                const next = e.target.checked;
                setBooks((b) => ({ ...b, [current.id]: next }));
                const existing = answers[current.id];
                if (existing) void saveAnswer(current, existing, next);
              }}
            />
            <span className="text-sm text-muted">
              I looked this up in a reference book
              <span className="mt-0.5 block text-xs text-muted">
                Self-reported. Used to show where your recall is thin — it is an approximation, not a
                proctored measurement.
              </span>
            </span>
          </label>
        ) : null}
      </article>

      {/* Above `sm` the pager sits in the flow; below it, it is fixed (see end). */}
      <div className="mt-5 hidden items-center gap-3 sm:flex">
        <button type="button" className="btn-secondary" onClick={() => goTo(index - 1)} disabled={atStart}>
          Previous
        </button>
        <button type="button" className="btn-primary" onClick={() => goTo(index + 1)} disabled={atEnd}>
          Next question
        </button>
        <span className="ml-auto text-xs text-subtle">{saving ? 'Saving…' : 'Saved'}</span>
      </div>

      {/*
        Question navigator — jumping around is allowed in the real paper too.
        Open on a wide screen, where it costs nothing; collapsed on a phone,
        where 120 chips at a 44px tap target is a thousand pixels of scroll
        between the candidate and the thing they were reading.
      */}
      <details key={isWide ? 'wide' : 'narrow'} className="mt-6 sm:mt-8" open={isWide}>
        <summary className="mb-2.5 inline-flex min-h-11 cursor-pointer list-none items-center text-xs font-semibold uppercase tracking-wide text-muted sm:min-h-0">
          All questions
          <span className="ml-2 font-normal normal-case tracking-normal sm:hidden">
            ({answeredCount}/{questions.length} answered)
          </span>
        </summary>
        <nav aria-label="Question navigator">
          <ol className="flex flex-wrap gap-1.5">
            {questions.map((q, i) => {
              const done = answers[q.id] !== undefined && answers[q.id] !== '';
              return (
                <li key={q.id}>
                  <button
                    type="button"
                    onClick={() => goTo(i)}
                    aria-current={i === index}
                    aria-label={`Question ${i + 1}${done ? ', answered' : ''}`}
                    className={`h-11 w-11 rounded text-sm font-semibold tabular-nums transition sm:h-8 sm:w-8 sm:text-xs ${
                      i === index
                        ? 'bg-brand-700 text-white'
                        : done
                          ? 'bg-brand-100 dark:bg-brand-500/20 text-brand-800 dark:text-brand-300 hover:bg-brand-200 dark:hover:bg-brand-500/30'
                          : 'bg-raised text-muted hover:bg-line'
                    }`}
                  >
                    {i + 1}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
      </details>

      <div className="mt-6 sm:hidden">
        {submitButton('btn-primary w-full')}
        <p className="mt-2 text-center text-xs text-subtle">{saving ? 'Saving…' : 'All answers saved'}</p>
      </div>

      {/*
        Fixed pager, phones only. Prev/Next is pressed once per question — up
        to 120 times in an OPRA paper — so it belongs under the thumb rather
        than below five answer options and a book-use checkbox.
      */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 pb-safe backdrop-blur sm:hidden">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-safe py-2.5">
          <button type="button" className="btn-secondary flex-1" onClick={() => goTo(index - 1)} disabled={atStart}>
            Previous
          </button>
          <button type="button" className="btn-primary flex-1" onClick={() => goTo(index + 1)} disabled={atEnd}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

/** Renders **bold** determiners (CORRECT, MOST, LEAST, NOT) inside a stem. */
function Stem({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
